import React, {useRef, useState} from "react";
import {GripVertical} from "lucide-react";


export type Pane = {
    key: string;
    size: number;               // starting share of the width, relative to the other panes
    node: React.ReactNode;
}

// the smallest share a drag leaves a pane
const MIN = 10;

/**
 * Columns side by side with drag handles between them. Panes can come and go (pass false to leave one out) -
 * the others keep their sizes. Fills its parent's height.
 * */
export function SplitPanes({panes}: { panes: (Pane | false)[] }) {
    const shown = panes.filter((p): p is Pane => !!p);
    const ref = useRef<HTMLDivElement>(null);
    // dragged sizes by pane key - panes not dragged yet use their own size
    const [sizes, setSizes] = useState<Record<string, number>>({});
    const [dragging, setDragging] = useState<boolean>(false);
    const sizeOf = (p: Pane) => sizes[p.key] ?? p.size;

    // the handle between shown[i] and shown[i + 1] - one grows, the other shrinks
    const startDrag = (i: number, e: React.MouseEvent) => {
        e.preventDefault();
        const left = shown[i];
        const right = shown[i + 1];
        const startX = e.clientX;
        const a = sizeOf(left);
        const b = sizeOf(right);
        // pixels -> size units
        const total = shown.reduce((sum, p) => sum + sizeOf(p), 0);
        const scale = total / (ref.current as HTMLDivElement).getBoundingClientRect().width;

        const onMouseMove = (ev: MouseEvent) => {
            const d = Math.max(MIN - a, Math.min(b - MIN, (ev.clientX - startX) * scale));
            setSizes((s) => ({...s, [left.key]: a + d, [right.key]: b - d}));
        };
        const onMouseUp = () => {
            setDragging(false);
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        };
        setDragging(true);
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    };

    return <div ref={ref} className="flex flex-1 min-h-0">
        {shown.map((p, i) =>
            <React.Fragment key={p.key}>
                {i > 0 &&
                    <div className="relative w-3 shrink-0 cursor-col-resize group" onMouseDown={(e) => startDrag(i - 1, e)}>
                        <div className="absolute inset-y-0 left-1/2 w-px bg-border group-hover:bg-primary"/>
                        <GripVertical className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-background text-muted-foreground"/>
                    </div>}
                <div className="min-w-0 min-h-0" style={{flex: `${sizeOf(p)} 1 0%`}}>
                    {p.node}
                </div>
            </React.Fragment>)}
        {/* while dragging - an iframe (a pdf) under the mouse would swallow the moves */}
        {dragging && <div className="fixed inset-0 z-50 cursor-col-resize"/>}
    </div>
}
